import React from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Stack,
  useTheme,
  Fade,
  TextField,
  InputAdornment,
  alpha,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import SearchIcon from "@mui/icons-material/Search";

const monthNames = Array.from({ length: 12 }, (_, i) =>
  new Date(0, i).toLocaleString("default", { month: "long" })
);

export default function ReceiptsTableToolbar({
  period = "month",
  setPeriod,
  month,
  setMonth,
  year,
  setYear,
  searchTerm,
  setSearchTerm,
  availableYears = [],
  sx = {},
  showSearch = true,
}) {
  const theme = useTheme();
  const thisYear = new Date().getFullYear();
  const years =
    availableYears && availableYears.length
      ? availableYears
      : Array.from({ length: 5 }, (_, i) => thisYear - i);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems="center"
      spacing={2}
      sx={{
        mb: 3,
        position: "relative",
        flexWrap: "wrap",
        gap: 2,
        ...sx,
      }}
    >
      {/* Period Toggle */}
      <ToggleButtonGroup
        value={period}
        exclusive
        onChange={(_, val) => val && setPeriod(val)}
        color="primary"
        size="small"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          "& .MuiToggleButton-root": {
            border: "none",
            fontWeight: 600,
            fontSize: 13,
            px: 2,
            py: 1,
            gap: 0.5,
            textTransform: "none",
            "&.Mui-selected": {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              },
            },
          },
        }}
      >
        <ToggleButton value="month">
          <CalendarMonthIcon fontSize="small" />
          Month
        </ToggleButton>
        <ToggleButton value="year">
          <EventNoteIcon fontSize="small" />
          Year
        </ToggleButton>
        <ToggleButton value="all">
          <AllInclusiveIcon fontSize="small" />
          All
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Month Selector */}
      <Fade in={period === "month"} unmountOnExit>
        <Box>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={month}
              label="Month"
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {monthNames.map((name, idx) => (
                <MenuItem key={name} value={idx}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Fade>

      {/* Year Selector */}
      <Fade in={period === "month" || period === "year"} unmountOnExit>
        <Box>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={year}
              label="Year"
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((yr) => (
                <MenuItem key={yr} value={yr}>
                  {yr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Fade>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Search Bar */}
      {showSearch && (
        <TextField
          size="small"
          placeholder="Search receipts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            minWidth: 250,
            "& .MuiOutlinedInput-root": {
              bgcolor: "background.paper",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
    </Stack>
  );
}
